import React, { type FC } from 'react';
import { NavLink } from 'react-router-dom';

import { FetchResult } from '@apollo/client';
import { Form, Formik, FormikErrors } from 'formik';

import { Button, Col, Row, Breadcrumb, NavLinkButton } from '@shared/components/bootstrap';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';
import { FormikNumberFormGroup } from '@shared/components/fields/formik/formik_number_input_group';
import { FormikSelectFormGroup } from '@shared/components/fields/formik/formik_select_form_group';
import { InputFormControl } from '@shared/components/fields/input_form_control';

import { useErrors, Errors } from '@admin/components/errors';
import { FormikRadioFormGroup } from '@admin/components/fields/formik/formik_radio_form_group';
import { Panel } from '@admin/components/helpers/panel';
import { DEFAULT_STATES } from '@admin/constants/geography';
import { AffiliateDefaultsFragment, AffiliateInput, AffiliateAddressInput, Scalars, Status } from '@admin/schema';

type AffiliateFormValues = {
  kind: string;
  name: string;
  email: string;
  phone: string;
  payout: number | undefined;
  discount: string;
  street: string;
  aptsuite: string;
  city: string;
  state: string;
  zip: string;
};

const AFFILIATE_KINDS = [
  { value: 'Affiliate', label: 'Affiliate' },
  { value: 'Ambassador', label: 'Ambassador' },
];

const ADDRESS_FIELDS = ['street', 'city', 'state', 'zip'] as const;

const hasAnyAddressValue = (values: AffiliateFormValues): boolean =>
  Boolean(
    values.street?.trim() ||
    values.aptsuite?.trim() ||
    values.city?.trim() ||
    values.state?.trim() ||
    values.zip?.trim(),
  );

const validate = (values: AffiliateFormValues): FormikErrors<AffiliateFormValues> => {
  const errors: FormikErrors<AffiliateFormValues> = {};
  if (hasAnyAddressValue(values)) {
    for (const field of ADDRESS_FIELDS) {
      if (!values[field]?.trim()) {
        errors[field] = 'Required';
      }
    }
  }
  return errors;
};

const AFFILIATES_URL = '/marketing/affiliates';

export const AffiliateForm: FC<{
  saving: boolean;
  discountOptions: number[];
  affiliate?: AffiliateDefaultsFragment;
  save(
    input: AffiliateInput,
    address: AffiliateAddressInput,
  ): Promise<
    FetchResult<{
      result: {
        status: Status;
        error?: string | null;
      };
    }>
  >;
}> = ({ saving, discountOptions, affiliate, save }) => {
  const errors = useErrors();

  const initialValues: AffiliateFormValues = {
    kind: affiliate?.kind ?? 'Affiliate',
    name: affiliate?.name ?? '',
    email: affiliate?.email ?? '',
    phone: affiliate?.phone ?? '',
    payout: affiliate?.payout ?? undefined,
    discount: affiliate?.discount != null ? String(affiliate.discount) : '',
    street: affiliate?.address?.street ?? '',
    aptsuite: affiliate?.address?.aptsuite ?? '',
    city: affiliate?.address?.city ?? '',
    state: affiliate?.address?.state ?? '',
    zip: affiliate?.address?.zip ?? '',
  };

  const onSubmit = async (values: AffiliateFormValues) => {
    if (saving) return;

    const input: AffiliateInput = {
      kind: values.kind,
      name: values.name,
      email: values.email,
      phone: values.phone,
      payout: values.payout ?? 0,
      discount: values.discount !== '' ? Number(values.discount) : undefined,
    };

    const address: AffiliateAddressInput = {
      street: values.street,
      aptsuite: values.aptsuite,
      city: values.city,
      state: values.state,
      zip: values.zip,
    };

    const response = await save(input, address);

    if (response.errors?.length) {
      const [error] = response.errors;
      errors.display(error);
      return;
    }

    if (response.data?.result.error) {
      errors.display(response.data.result.error);
      return;
    }

    if (response.data?.result.status === Status.Ok) {
      window.location.href = AFFILIATES_URL;
    }
  };

  return (
    <>
      <div className="page-header">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to={AFFILIATES_URL}>Affiliates</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{affiliate ? 'Edit' : 'New'}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
        {({ values }) => (
          <Form>
            <Errors {...errors} />
            <Panel>
              <Panel.Header>
                <Panel.Title>Details</Panel.Title>
              </Panel.Header>
              <Panel.Body>
                <Row>
                  <Col sm={6}>
                    {affiliate ? (
                      <FormikInputFormGroup label="Type" name="kind" value={values.kind} disabled />
                    ) : (
                      <FormikRadioFormGroup id="affiliate_kind" label="Type" name="kind" options={AFFILIATE_KINDS} />
                    )}
                    <FormikInputFormGroup id="affiliate_name" label="Name" name="name" required />
                    <FormikInputFormGroup id="affiliate_email" label="Email" name="email" type="email" required />
                    <FormikInputFormGroup id="affiliate_phone" label="Phone" name="phone" required />
                    <FormikNumberFormGroup
                      id="affiliate_payout"
                      label="Payout"
                      name="payout"
                      required
                      allowDecimal
                      help={`Payout to the ${values.kind}`}
                    />
                    <FormikSelectFormGroup id="affiliate_discount" label="Discount" name="discount" required>
                      <option value="">- Please choose an Amount -</option>
                      {values.kind === 'Affiliate' && <option value="0">$0.00</option>}
                      {discountOptions.map((amount) => (
                        <option key={amount} value={String(amount)}>
                          ${amount.toFixed(2)}
                        </option>
                      ))}
                    </FormikSelectFormGroup>
                  </Col>
                  <Col sm={6}>
                    <h4>Address</h4>
                    <FormikInputFormGroup id="affiliate_street" label="Street" name="street" />
                    <FormikInputFormGroup id="affiliate_aptsuite" label="Apt / Suite" name="aptsuite" />
                    <Row>
                      <Col sm={5}>
                        <FormikInputFormGroup id="affiliate_city" label="City" name="city" />
                      </Col>
                      <Col sm={4}>
                        <FormikSelectFormGroup id="affiliate_state" label="State" name="state">
                          <option value="">- Select -</option>
                          {DEFAULT_STATES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </FormikSelectFormGroup>
                      </Col>
                      <Col sm={3}>
                        <FormikInputFormGroup id="affiliate_zip" label="ZIP" name="zip" />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Panel.Body>
              <Panel.Footer align="right">
                <NavLinkButton kind="default" to={AFFILIATES_URL} disabled={saving}>
                  Cancel
                </NavLinkButton>{' '}
                <Button type="submit" kind="primary" loading={saving}>
                  Save
                </Button>
              </Panel.Footer>
            </Panel>
          </Form>
        )}
      </Formik>
    </>
  );
};
